import { apiFetch } from "./client";

/**
 * The quality-parameter codebook. What the lab measures is
 * data, not schema — reading this list is how the intake form is built.
 */
export type QualityParameter = {
  id: string;
  /** Stable machine key. Never changes after creation. */
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  sortOrder: number;
  active: boolean;
  updatedAt: string;
  /** NUMERIC, BOOLEAN (GMO, presence of infestation) or TEXT. Intrinsic to the test, not to its use. */
  resultType?: QualityResultType;
  /**
   * Which lab discipline performs it: CHEMISTRY, MICROBIOLOGY or GRAIN.
   *
   * It decides more than grouping — microbiology returns a result days after chemistry, so a point that
   * mixes the two is a point whose results arrive in two waves.
   */
  discipline?: string;
};

export const qualityApi = {
  listParameters: (activeOnly = true) =>
    apiFetch<QualityParameter[]>(`/quality/parameters?activeOnly=${activeOnly}`),
  createParameter: (body: {
    key: string;
    nameI18n?: Record<string, string>;
    unit?: string;
    decimals?: number;
    sortOrder?: number;
  }) => apiFetch<QualityParameter>("/quality/parameters", { method: "POST", body }),
  updateParameter: (
    id: string,
    body: {
      nameI18n?: Record<string, string>;
      unit?: string;
      decimals?: number;
      sortOrder?: number;
      active?: boolean;
    },
  ) => apiFetch<QualityParameter>(`/quality/parameters/${id}`, { method: "PUT", body }),
  deactivateParameter: (id: string) =>
    apiFetch<void>(`/quality/parameters/${id}`, { method: "DELETE" }),
};

/* ------------------------------------------------------------------ koraci proizvodnje */

/**
 * A named part of the plant the soy passes through: priprema, ekstrakcija, mlevenje, lecitin.
 *
 * The old ERP keeps no list of production steps at all — these four come from its equipment register,
 * the one place where production is divided into named units. The list is openly incomplete (toasting,
 * pelleting and texturising are visible in product names but sit in no plant), which is why it is a
 * codebook somebody can add to rather than a fixed enumeration.
 */
export type ProductionStep = {
  id: string;
  code: string;
  nameI18n: Record<string, string> | null;
  sortOrder: number;
  active: boolean;
  note: string | null;
  /** How many plan items point at the step. A step with none can still be renamed freely. */
  timesPrescribed: number;
};

export const productionStepApi = {
  list: (activeOnly = true) =>
    apiFetch<ProductionStep[]>(`/quality/production-steps?activeOnly=${activeOnly}`),
  create: (body: { code: string; nameI18n?: Record<string, string>; sortOrder?: number; note?: string }) =>
    apiFetch<ProductionStep>("/quality/production-steps", { method: "POST", body }),
  update: (
    id: string,
    body: { nameI18n?: Record<string, string>; sortOrder?: number; active?: boolean; note?: string },
  ) => apiFetch<ProductionStep>(`/quality/production-steps/${id}`, { method: "PUT", body }),
  retire: (id: string) => apiFetch<void>(`/quality/production-steps/${id}`, { method: "DELETE" }),
};

/* ------------------------------------------------------------------ konfigurator testova */

/**
 * Where in the chain a test is done. The same test runs in several places — moisture on the inbound
 * trip before weighing, again in production, again on the finished product — so the phase is what
 * separates those uses, and the catalogue above holds the test only once.
 */
export const QUALITY_PHASES = ["INTAKE", "PROCESS", "SEMI_FINISHED", "FINISHED", "DISPATCH"] as const;
export type QualityPhase = (typeof QUALITY_PHASES)[number];

/** How a result is written down. The portal had only numbers, so GMO could not be entered at all. */
export const RESULT_TYPES = ["NUMERIC", "BOOLEAN", "TEXT"] as const;
export type QualityResultType = (typeof RESULT_TYPES)[number];

/**
 * What a frequency is counted against. Three different bases arrive in one sentence of the answer
 * („hemijske analize 1po, ... u svakom drugom uzorku palete, mikrobiologija 5x po lotu"), so they
 * stay apart instead of collapsing into one number.
 */
export const FREQUENCY_BASES = [
  "PER_TRUCK",
  "PER_LOT",
  "PER_PALLET",
  "PER_BATCH",
  "DAILY",
  "EVERY_N_MONTHS",
] as const;
export type QualityFrequencyBasis = (typeof FREQUENCY_BASES)[number];

/** What happens past the acceptance limit. Null means the limit itself is not on file yet. */
export const OUT_OF_RANGE_ACTIONS = [
  "REFUSE",
  "SETTLE_WITH_PENALTY",
  "APPROVAL_REQUIRED",
  "HOLD_LOT",
] as const;
export type OutOfRangeAction = (typeof OUT_OF_RANGE_ACTIONS)[number];

/** A test in the catalogue, with the phases it is actually used in. */
export type QualityCatalogueEntry = {
  id: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  resultType: QualityResultType;
  active: boolean;
  usedInPhases: QualityPhase[];
  timesPrescribed: number;
};

export type PlanItem = {
  id: string;
  phase: QualityPhase;
  parameterId: string | null;
  parameterKey: string | null;
  parameterNameI18n: Record<string, string> | null;
  unit: string | null;
  decimals: number | null;
  resultType: QualityResultType | null;
  /** Set instead of the test when the item is an inspection the portal does not measure. */
  description: string | null;
  method: string | null;
  mandatory: boolean;
  technicalMin: number | null;
  technicalMax: number | null;
  usualMin: number | null;
  usualMax: number | null;
  acceptMin: number | null;
  acceptMax: number | null;
  /** Prihvaćena strana da-ne testa. Prazno na brojčanom. */
  acceptBool: boolean | null;
  outOfRangeAction: OutOfRangeAction | null;
  frequencyBasis: QualityFrequencyBasis;
  frequencyCount: number;
  enteredByRole: string | null;
  checkedByRole: string | null;
  external: boolean;
  sortOrder: number;
  note: string | null;
  /** True when no acceptance limit is on file — the screen says so instead of implying it passes anything. */
  limitMissing: boolean;
  /**
   * Na kom se koraku proizvodnje test radi. Prazno van faze procesa, i prazno u procesu dok korak nije
   * određen: vlaga posle ekstrakcije i vlaga posle mlevenja su dve propisane stavke, ne jedna.
   */
  productionStepId: string | null;
  productionStepCode: string | null;
};

export type PhaseCount = {
  phase: QualityPhase;
  tests: number;
  descriptive: number;
  external: number;
  limitMissing: number;
};

export type TestPlan = {
  id: string;
  code: string;
  nameI18n: Record<string, string> | null;
  /** A plan is for a culture (raw material) or a catalog product; neither means it applies to all. */
  culture: string | null;
  productId: string | null;
  validFrom: string;
  validTo: string | null;
  active: boolean;
  note: string | null;
  inForce: boolean;
  phases: PhaseCount[];
  /** Empty on the list endpoint; filled when one plan is opened. */
  items: PlanItem[];
};

export type PlanItemBody = {
  phase?: QualityPhase;
  /**
   * Šalje se samo pri dodavanju i samo u fazi procesa. Pri izmeni ga nema namerno: premeštanje testa sa
   * jednog koraka na drugi je druga propisana stavka, pa se stara skloni i upiše nova.
   */
  productionStepId?: string | null;
  parameterId?: string | null;
  description?: string | null;
  method?: string | null;
  mandatory?: boolean;
  technicalMin?: number | null;
  technicalMax?: number | null;
  usualMin?: number | null;
  usualMax?: number | null;
  acceptMin?: number | null;
  acceptMax?: number | null;
  acceptBool?: boolean | null;
  outOfRangeAction?: OutOfRangeAction | null;
  frequencyBasis?: QualityFrequencyBasis;
  frequencyCount?: number;
  enteredByRole?: string | null;
  checkedByRole?: string | null;
  external?: boolean;
  sortOrder?: number;
  note?: string | null;
};

/**
 * Jedan plan i ono što propisuje u jednoj fazi.
 *
 * Pogled po fazi grupiše po planu, a ne nabraja ravno: „šta se radi na prijemu" nije jedan spisak nego
 * po jedan za svaku sirovinu, a ravan spisak bi sedam testova soje i četiri ječma prikazao kao jedan
 * skup od jedanaest.
 */
export type PhasePlanGroup = {
  planId: string;
  planCode: string;
  planNameI18n: Record<string, string> | null;
  culture: string | null;
  productId: string | null;
  items: PlanItem[];
};

/** Sve što je propisano u jednoj fazi, kroz sve planove koji važe. */
export type PhaseView = {
  phase: QualityPhase;
  tests: number;
  groups: PhasePlanGroup[];
};

/** Koliko testova propisuje svaka faza — broj koji birač faze nosi pre nego što se faza otvori. */
export type PhaseTally = {
  phase: QualityPhase;
  tests: number;
};

export const testPlanApi = {
  catalogue: (activeOnly = true) =>
    apiFetch<QualityCatalogueEntry[]>(`/quality/test-plans/catalogue?activeOnly=${activeOnly}`),

  list: () => apiFetch<TestPlan[]>("/quality/test-plans"),

  /** Drugi ulaz u konfigurator: šta je propisano u jednoj fazi, kroz sve planove koji važe. */
  byPhase: (phase: QualityPhase) =>
    apiFetch<PhaseView>(`/quality/test-plans/by-phase?phase=${phase}`),

  phaseTally: () => apiFetch<PhaseTally[]>("/quality/test-plans/phase-tally"),

  get: (id: string) => apiFetch<TestPlan>(`/quality/test-plans/${id}`),

  create: (body: {
    code: string;
    nameI18n?: Record<string, string>;
    culture?: string | null;
    productId?: string | null;
    validFrom?: string;
    validTo?: string | null;
    note?: string | null;
  }) => apiFetch<TestPlan>("/quality/test-plans", { method: "POST", body }),

  update: (
    id: string,
    body: {
      nameI18n?: Record<string, string>;
      validFrom?: string;
      validTo?: string | null;
      active?: boolean;
      note?: string | null;
    },
  ) => apiFetch<TestPlan>(`/quality/test-plans/${id}`, { method: "PUT", body }),

  retire: (id: string) => apiFetch<void>(`/quality/test-plans/${id}`, { method: "DELETE" }),

  addItem: (planId: string, body: PlanItemBody) =>
    apiFetch<PlanItem>(`/quality/test-plans/${planId}/items`, { method: "POST", body }),

  updateItem: (planId: string, itemId: string, body: PlanItemBody) =>
    apiFetch<PlanItem>(`/quality/test-plans/${planId}/items/${itemId}`, { method: "PUT", body }),

  removeItem: (planId: string, itemId: string) =>
    apiFetch<void>(`/quality/test-plans/${planId}/items/${itemId}`, { method: "DELETE" }),
};

/* ------------------------------------------------------------------ nalazi po fazama */

/**
 * Nalaz van prijema: u procesu, na poluproizvodu, na gotovom proizvodu ili pri otpremi.
 *
 * Nalaz NA PRIJEMU nije ovde — on ispravlja novac i stoji na isporuci, pa ga obračun kooperantu čita
 * tamo. Pokušaj unosa prijemnog nalaza ovde servis odbija i kaže gde pripada.
 */
export const READING_STATUSES = ["RECORDED", "PENDING_VERIFICATION", "VERIFIED"] as const;
export type QualityReadingStatus = (typeof READING_STATUSES)[number];

export type QualityReading = {
  id: string;
  phase: QualityPhase;
  parameterId: string;
  parameterKey: string;
  parameterNameI18n: Record<string, string> | null;
  unit: string;
  resultType: QualityResultType;
  value: number | null;
  valueBool: boolean | null;
  valueText: string | null;
  displayValue: string;
  sampleLabel: string | null;
  palletNo: number | null;
  shiftLabel: string | null;
  measuredAt: string;
  status: QualityReadingStatus;
  /** Vrednost je bila van prijemne granice u trenutku unosa; kasnija izmena granice ne menja istoriju. */
  outOfRange: boolean;
  recordedBy: string;
  recordedAt: string;
  verifiedBy: string | null;
  verifiedAt: string | null;
  note: string | null;
  /** Nad čim je izmereno: popunjeno je jedno od dva. Red za potpis bez LOT-a nema šta da otvori. */
  goodsLotId: string | null;
  goodsLotDesignation: string | null;
  productionOrderId: string | null;
};

/** Jedna propisana stavka prema onome što je izmereno — red koji šef laboratorije potpisuje. */
export type CoverageRow = {
  planItemId: string;
  phase: QualityPhase;
  parameterKey: string | null;
  parameterNameI18n: Record<string, string> | null;
  description: string | null;
  mandatory: boolean;
  external: boolean;
  /** Osnova i broj odvojeno: ekran ih prevodi, a sklopljen niz nije mogao ni da prevede ni da pročita. */
  frequencyBasis: QualityFrequencyBasis;
  frequencyCount: number;
  required: number;
  done: number;
  complete: boolean;
  /** Plan je propisuje, ali granica nije uneta, pa se ni po čemu ne može suditi. */
  limitMissing: boolean;
};

/**
 * Potpis šefa laboratorije na pokrivenost (KK29) sa brojevima kakvi su bili u tom trenutku.
 *
 * Pokrivenost se računa pri svakom čitanju; potpis je tvrdnja o jednom trenutku, pa se brojevi u njemu
 * ne preračunavaju. Nalaz upisan posle potpisa ne menja potpis — menja samo `signatureOutdated`.
 */
export type CoverageSignature = {
  id: string;
  signedBy: string;
  signedAt: string;
  requiredCount: number;
  doneCount: number;
  pallets: number | null;
  /** Postoji tačno kad pokrivenost nije bila puna: šta je manjkalo i na čiju reč je potpisano. */
  note: string | null;
};

export type CoverageView = {
  planId: string;
  planCode: string;
  phase: QualityPhase;
  pallets: number | null;
  rows: CoverageRow[];
  requiredTotal: number;
  doneTotal: number;
  /**
   * Sve obavezno propisano je i odgovoreno. Stavka koja se ne može oceniti ne računa se kao
   * odgovorena — ni ona po paleti dok broj paleta nije poznat, ni ona za koju portal ne drži nalaz
   * jer se meri spolja ili se pregleda a ne meri.
   */
  complete: boolean;
  /** Zadnji potpis na ovom predmetu i fazi, ili null dok nije potpisano. */
  signature: CoverageSignature | null;
  /** Tačno kad se ono što je sad u portalu razlikuje od potpisanog — potpis je stariji od činjenica. */
  signatureOutdated: boolean;
};

/** Potpisivanje pokrivenosti. Broj paleta je deo tvrdnje, a obrazloženje obavezno kad nije puna. */
export type VerifyCoverageBody = {
  planId: string;
  phase: QualityPhase;
  pallets?: number | null;
  note?: string | null;
};

export type RecordReadingBody = {
  phase: QualityPhase;
  parameterId: string;
  planItemId?: string | null;
  value?: number | null;
  valueBool?: boolean | null;
  valueText?: string | null;
  sampleLabel?: string | null;
  palletNo?: number | null;
  shiftLabel?: string | null;
  measuredAt?: string | null;
  note?: string | null;
};

/** Jedna vrednost na radnom listu. Uzorak i paleta stoje na listu, ne ovde. */
export type SheetValue = {
  parameterId: string;
  planItemId?: string | null;
  productionStepId?: string | null;
  value?: number | null;
  valueBool?: boolean | null;
  valueText?: string | null;
  note?: string | null;
};

/**
 * Radni list: jedan uzorak, sve vrednosti koje plan propisuje za tu fazu, jedno čuvanje.
 *
 * Zapisuje se u jednoj transakciji — ili ulazi ceo ili nijedan red, jer je radni list jedan čin.
 */
export type RecordSheetBody = {
  phase: QualityPhase;
  sampleLabel?: string | null;
  palletNo?: number | null;
  shiftLabel?: string | null;
  measuredAt?: string | null;
  /** Napomena o uzorku; spušta se na svaku vrednost koja svoju nema. */
  note?: string | null;
  values: SheetValue[];
};

export const qualityReadingApi = {
  forLot: (goodsLotId: string) => apiFetch<QualityReading[]>(`/quality/readings/lots/${goodsLotId}`),

  forProductionOrder: (id: string) =>
    apiFetch<QualityReading[]>(`/quality/readings/production-orders/${id}`),

  /** Red za potpis šefa laboratorije: sve što je odstupilo od granice ili je izmenjeno. */
  awaitingVerification: () =>
    apiFetch<QualityReading[]>("/quality/readings/awaiting-verification"),

  recordSheetForLot: (goodsLotId: string, body: RecordSheetBody) =>
    apiFetch<QualityReading[]>(`/quality/readings/lots/${goodsLotId}/sheet`, { method: "POST", body }),

  recordForProductionOrder: (id: string, body: RecordReadingBody) =>
    apiFetch<QualityReading>(`/quality/readings/production-orders/${id}`, { method: "POST", body }),

  verify: (readingId: string) =>
    apiFetch<QualityReading>(`/quality/readings/${readingId}/verify`, { method: "POST" }),

  coverage: (goodsLotId: string, planId: string, phase: QualityPhase, pallets?: number | null) =>
    apiFetch<CoverageView>(
      `/quality/readings/lots/${goodsLotId}/coverage?planId=${planId}&phase=${phase}` +
        (pallets != null ? `&pallets=${pallets}` : ""),
    ),

  /** Potpis šefa laboratorije na pokrivenost plana za ovaj LOT i fazu (KK29). */
  signCoverage: (goodsLotId: string, body: VerifyCoverageBody) =>
    apiFetch<CoverageView>(`/quality/readings/lots/${goodsLotId}/coverage/verify`, {
      method: "POST",
      body,
    }),
};

/** Šifra i naziv proizvoda, bez cena. Kvalitet treba da ume da imenuje proizvod, ne da čita katalog. */
export type ProductPick = {
  id: string;
  sku: string;
  name: string;
};

export const qualityProductApi = {
  search: (q: string, locale = "sr", limit = 20) =>
    apiFetch<ProductPick[]>(
      `/quality/products?q=${encodeURIComponent(q)}&locale=${locale}&limit=${limit}`,
    ),
};

/** Jedan lot koji čeka obradu: propisan test na njemu još nema nalaz. */
export type LotQueueRow = {
  lotId: string;
  designation: string | null;
  barcode: string | null;
  productName: string | null;
  erpProductId: string | null;
  producedOn: string;
  planId: string;
  planCode: string;
  /** Ime plana kako ga čovek čita; šifra je za mašinu. */
  planNameI18n: Record<string, string> | null;
  /** Koliko testova plan propisuje u toj fazi koji MOGU da nose nalaz (obavezni, ne spoljni). */
  prescribed: number;
  /** Koliko od njih ima bar jedan nalaz. */
  started: number;
  missing: number;
};

/** Jedan izbor u filteru robe. */
export type QueueProduct = { erpProductId: string; name: string | null };

export const workQueueApi = {
  page: (
    strana: number,
    velicina: number,
    f: { from: string; to: string; phase: QualityPhase; q?: string; product?: string },
  ) => {
    const p = new URLSearchParams({
      page: String(strana),
      size: String(velicina),
      from: f.from,
      to: f.to,
      phase: f.phase,
    });
    if (f.q && f.q.trim()) p.set("q", f.q.trim());
    if (f.product) p.set("product", f.product);
    return apiFetch<{
      content: LotQueueRow[];
      totalElements: number;
      totalPages: number;
      number: number;
      size: number;
    }>(`/quality/readings/queue?${p.toString()}`);
  },

  products: (from: string, to: string) =>
    apiFetch<QueueProduct[]>(
      `/quality/readings/queue/products?${new URLSearchParams({ from, to }).toString()}`,
    ),

};
